import { useEffect, useState } from "react";
import { useNavigate, useLocation, Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { useAuth } from "../context/AuthContext";
import { formatApiErrorDetail } from "../lib/api";
import { Reveal } from "../components/Motion";

const BG = "https://static.prod-images.emergentagent.com/jobs/9f155735-a3e2-49f2-ade7-372db22ee705/images/6f65a75de4ff2b304ad5099e313b467cf02a758c0827650fb2d094e5ff672384.jpeg";

export default function Auth({ mode = "login" }) {
  const [isRegister, setIsRegister] = useState(mode === "register");
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const { user, login, register } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const next = new URLSearchParams(location.search).get("next") || "/account";

  useEffect(() => {
    document.title = `${isRegister ? "Create Account" : "Sign In"} — Motion & Meaning`;
  }, [isRegister]);

  useEffect(() => {
    if (user) navigate(next, { replace: true });
  }, [user, next, navigate]);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      if (isRegister) await register(name, email, password);
      else await login(email, password);
      navigate(next, { replace: true });
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
      setLoading(false);
    }
  };

  return (
    <section className="relative min-h-screen flex items-center overflow-hidden" data-testid="auth-page">
      <img src={BG} alt="" aria-hidden="true" className="absolute inset-0 w-full h-full object-cover opacity-20" />
      <div className="absolute inset-0 bg-gradient-to-r from-[#050505] via-[#050505]/90 to-[#050505]/50" />
      <div className="mm-vignette" />

      <div className="relative z-10 max-w-[1440px] mx-auto px-6 md:px-12 w-full grid grid-cols-1 md:grid-cols-2 gap-16 items-center pt-32 pb-24">
        <div className="hidden md:block">
          <Reveal>
            <p className="font-mono text-[11px] tracking-[0.4em] text-gold mb-6">FAN ACCOUNT</p>
            <h1 className="font-serif text-6xl lg:text-7xl font-light leading-[0.9] text-white">
              Your place<br />in the <span className="italic">universe.</span>
            </h1>
            <p className="text-white/60 leading-relaxed mt-8 max-w-md">
              An account keeps your orders, downloads and access in one place. Optional — you never
              need one to purchase.
            </p>
          </Reveal>
        </div>

        <Reveal delay={0.15} className="flex md:justify-end">
          <div className="w-full max-w-md">
            <div className="flex gap-8 mb-10">
              <button
                onClick={() => setIsRegister(false)}
                data-testid="tab-login"
                className={`font-mono text-[11px] tracking-[0.25em] pb-2 border-b transition-colors ${!isRegister ? "text-gold border-gold" : "text-white/40 border-transparent hover:text-white/70"}`}
              >
                SIGN IN
              </button>
              <button
                onClick={() => setIsRegister(true)}
                data-testid="tab-register"
                className={`font-mono text-[11px] tracking-[0.25em] pb-2 border-b transition-colors ${isRegister ? "text-gold border-gold" : "text-white/40 border-transparent hover:text-white/70"}`}
              >
                CREATE ACCOUNT
              </button>
            </div>

            <form onSubmit={submit} className="space-y-7" data-testid="auth-form">
              <AnimatePresence>
                {isRegister && (
                  <motion.div initial={{ opacity: 0, height: 0 }} animate={{ opacity: 1, height: "auto" }} exit={{ opacity: 0, height: 0 }}>
                    <label className="font-mono text-[10px] tracking-[0.25em] text-white/40 block mb-3">NAME</label>
                    <input
                      value={name}
                      onChange={(e) => setName(e.target.value)}
                      data-testid="auth-name"
                      className="w-full bg-transparent border-b border-white/20 focus:border-gold outline-none py-3 text-lg text-white transition-colors"
                      placeholder="Your name"
                    />
                  </motion.div>
                )}
              </AnimatePresence>
              <div>
                <label className="font-mono text-[10px] tracking-[0.25em] text-white/40 block mb-3">EMAIL</label>
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  data-testid="auth-email"
                  className="w-full bg-transparent border-b border-white/20 focus:border-gold outline-none py-3 text-lg text-white transition-colors"
                  placeholder="you@dimension.com"
                />
              </div>
              <div>
                <label className="font-mono text-[10px] tracking-[0.25em] text-white/40 block mb-3">PASSWORD</label>
                <input
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  data-testid="auth-password"
                  className="w-full bg-transparent border-b border-white/20 focus:border-gold outline-none py-3 text-lg text-white transition-colors"
                  placeholder="••••••••"
                />
              </div>

              {error && <p className="text-red-500 text-sm font-mono" data-testid="auth-error">{error}</p>}

              {!isRegister && (
                <div className="text-right">
                  <Link
                    to="/forgot-password"
                    data-testid="auth-forgot-link"
                    className="font-mono text-[10px] tracking-[0.25em] text-white/40 hover:text-gold transition-colors"
                  >
                    FORGOT PASSWORD?
                  </Link>
                </div>
              )}

              <button
                type="submit"
                disabled={loading}
                data-testid="auth-submit"
                className="w-full bg-gold text-black font-mono text-[11px] tracking-[0.25em] py-4 hover:bg-white transition-colors disabled:opacity-50"
              >
                {loading ? "ALIGNING..." : isRegister ? "CREATE ACCOUNT" : "SIGN IN"}
              </button>
            </form>

            <p className="text-white/40 text-sm mt-8">
              Just browsing? <Link to="/store" className="text-gold hover:underline">Continue as guest →</Link>
            </p>
          </div>
        </Reveal>
      </div>
    </section>
  );
}
